"use client"

import { useState, useRef, useEffect } from "react"
import { marked } from "marked"
import { convertSingleValue } from "../utils/commonUtils"
import { formatFiscalDateFromString } from "../utils/commonUtils"
import SelectBox from "./SelectBox"
import { LineChart, ChevronDown, ChevronUp } from "lucide-react"
import Icon from "../utils/Icon"
import ExchangeIcon from "../utils/ExchangeIcon"

const CompanyDescription = ({ description }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const THRESHOLD = 80
  const getShortDescription = (text) => {
    if (!text) {
      return ""
    }

    if (text.length <= THRESHOLD) {
      return text
    }
    return text.slice(0, THRESHOLD) + "..."
  }

  return (
    <div>
      <span
        dangerouslySetInnerHTML={{
          __html: isExpanded
            ? marked(description) // 展開した状態では全文表示
            : marked(getShortDescription(description)), // 折りたたみ状態では短い説明
        }}
      ></span>
      {description && description.length > THRESHOLD && (
        <button onClick={() => setIsExpanded(!isExpanded)} className="text-gray-400 text-xs ml-1 hover:underline">
          {isExpanded ? "折りたたむ" : "続きを読む"}
        </button>
      )}
    </div>
  )
}

const FileCompanyDescription = ({
  secCode,
  description,
  setCompanyDescription,
  handleDescriptionClick,
}) => {
  const [showPopover, setShowPopover] = useState(false)
  const popoverRef = useRef(null)

  const handleClick = (e) => {
    e.stopPropagation()
    setShowPopover(!showPopover)
    setCompanyDescription(description)
    if (!showPopover) {
      handleDescriptionClick(secCode)
    }
  }

  // ポップオーバー外のクリックを検知して閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setShowPopover(false)
      }
    }

    if (showPopover) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showPopover])

  return (
    <div className="w-full flex justify-center items-center relative">
      <div
        className="rounded rounded-full hover:bg-gray-200 flex p-2 w-[38px] cursor-pointer justify-center items-center"
        onClick={handleClick}
      >
        ...
      </div>

      {showPopover && (
        <div
          ref={popoverRef}
          className="absolute z-50 bg-white border rounded-lg shadow-lg p-4 w-[600px] right-0 mt-2 top-full"
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          <div className="text-sm font-bold mb-2">事業内容</div>
          <div
            className="text-sm"
            dangerouslySetInnerHTML={{ __html: marked(description || "情報がありません") }}
          ></div>
        </div>
      )}
    </div>
  )
}

const SearchTable = ({
  searchResults,
  onDashboardClick,
  selectedCompanies,
  onCompanyClick,
  addAllCompanies,
  addCompany,
  removeCompany,
  registeredCompanies,
  setCompanyDescription,
  handleDescriptionClick,
  isMobile,
}) => {
  const [showSelectAllTooltip, setShowSelectAllTooltip] = useState(false)
  const [showCompanyDescription, setShowCompanyDescription] = useState(false)
  const tableRef = useRef(null)
  const [expandedCards, setExpandedCards] = useState({})

  const onSelectAllClick = () => {
    const companies = searchResults.map((company) => ({
      secCode: company.sec_code,
      companyName: company.company_name,
    }))
    addAllCompanies(companies)
  }

  useEffect(() => {
    if (!tableRef.current) return
    // ResizeObserverを定義
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          if (entry.contentRect.width < 200) {
            setShowCompanyDescription(false)
          } else {
            setShowCompanyDescription(true)
          }
        }
      }
    })

    // ref要素を監視
    observer.observe(tableRef.current)

    // クリーンアップ処理
    return () => {
      if (tableRef.current) {
        observer.unobserve(tableRef.current)
      }
    }
  }, [])

  const toggleCardExpand = (secCode) => {
    setExpandedCards((prev) => ({
      ...prev,
      [secCode]: !prev[secCode],
    }))
  }

  return (
    <div id="searchCompanyTableContainer" className="companies-table-container scrollbar-hide">
      {/* PC用テーブル - スマホでは非表示 */}
      <div className={`table-margin ${isMobile ? "hidden" : "block"}`}>
        {showSelectAllTooltip && <div className="select-all-tooltip">すべて選択する</div>}
        <table id="searchCompaniesTable" className="companies-table w-full">
          <thead>
            <tr>
              <th
                className="th-graph-checkbox top-right-radius-5"
                onClick={onSelectAllClick}
                onMouseEnter={() => setShowSelectAllTooltip(true)}
                onMouseLeave={() => setShowSelectAllTooltip(false)}
              >
                <LineChart />
              </th>
              <th className="th-company-name top-left-radius-5">企業名</th>
              <th className="th-industry top-left-radius-5">業種</th>
              <th className="th-fiscal-year">決算年度</th>
              <th className="th-sales">売上高</th>
              <th className="th-business" ref={tableRef}>
                事業内容
              </th>
            </tr>
          </thead>
          <tbody>
            {searchResults.map((company) => (
              <tr key={company.sec_code}>
                <td className="td-graph-checkbox">
                  <SelectBox
                    secCode={company.sec_code}
                    companyName={company.company_name}
                    selectedCompanies={selectedCompanies}
                    onCompanyClick={onCompanyClick}
                  />
                </td>
                <td className="td-company-name">
                  <div className="flex">
                    <div className="mr-2 flex items-center">
                      <div className="w-[25px] h-[25px] ">
                        <Icon secCode={company.sec_code} companyImageUrl={company.image} />
                      </div>
                    </div>
                    <div>
                      <div className="dashboard-link py-2 mb-2" onClick={() => onDashboardClick(company.sec_code)}>
                        {company.company_name}
                      </div>
                      <div className="flex">
                        <div className="mr-2">
                          <ExchangeIcon exchange={company.exchangeshortname} />
                        </div>
                        <div>
                          {company.exchangeshortname}：{company.sec_code}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="td-industry">{company.industry_name}</td>
                <td className="td-fiscal-date">
                  {company.annotated_fiscal_date
                    ? formatFiscalDateFromString(company.annotated_fiscal_date, "YYYY年m月")
                    : ""}
                </td>
                <td className="td-sales">{convertSingleValue(company.sales)}</td>
                <td>
                  {showCompanyDescription ? (
                    <CompanyDescription description={company.company_description} />
                  ) : (
                    <FileCompanyDescription
                      secCode={company.sec_code}
                      description={company.company_description}
                      setCompanyDescription={setCompanyDescription}
                      handleDescriptionClick={handleDescriptionClick}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* スマホ用カードビュー - PCでは非表示 */}
      <div className={`${isMobile ? "block" : "hidden"} space-y-4 px-2 overflow-y-auto`}>
        {searchResults.map((company) => {
          const isExpanded = expandedCards[company.sec_code] || false

          return (
            <div key={company.sec_code} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-start gap-2">
                  <div className="w-[25px] h-[25px] mt-1">
                    <Icon secCode={company.sec_code} companyImageUrl={company.image} />
                  </div>
                  <div>
                    <div className="font-bold dashboard-link" onClick={() => onDashboardClick(company.sec_code)}>
                      {company.company_name}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <ExchangeIcon exchange={company.exchangeshortname} className="mr-1" />
                      <span className="ml-1">{company.exchangeshortname}：{company.sec_code}</span>
                    </div>
                  </div>
                </div>
                <SelectBox
                  secCode={company.sec_code}
                  companyName={company.company_name}
                  selectedCompanies={selectedCompanies}
                  onCompanyClick={onCompanyClick}
                />
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-3 mb-3">
                <div>
                  <span className="font-medium">決算年度:</span>{" "}
                  {company.annotated_fiscal_date
                    ? formatFiscalDateFromString(company.annotated_fiscal_date, "YYYY年m月")
                    : ""}
                </div>
                <div>
                  <span className="font-medium">売上高:</span> {convertSingleValue(company.sales)}
                </div>
                <div>
                  <span className="font-medium">業種:</span> {company.industry_name}
                </div>
              </div>

              {/* 事業内容 - 折りたたみ可能 */}
              <div className="mt-2 border-t pt-2 analysis-business-description">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => toggleCardExpand(company.sec_code)}
                >
                  <span className="text-xs font-bold">事業内容</span>
                  {isExpanded ? (
                    <ChevronUp size={16} className="text-gray-500" />
                  ) : (
                    <ChevronDown size={16} className="text-gray-500" />
                  )}
                </div>

                {isExpanded && (
                  <div className="mt-2 text-xs">
                    {company.company_description ? (
                      <div dangerouslySetInnerHTML={{ __html: marked(company.company_description) }}></div>
                    ) : (
                      <span className="text-gray-500">事業内容の詳細はありません</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SearchTable
